import { Component, Input, OnChanges, SimpleChanges, Inject } from '@angular/core';
import { BilledProduct, PricingTier } from '@vendasta/shared';
import { MarketplaceBillingFrequency, DisplayPriceService } from '@vendasta/shared';
import { Pricing, Price } from './pricing';
import { BehaviorSubject, combineLatest, Observable, of, EMPTY } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { map, switchMap, expand, reduce, catchError, publishReplay, refCount, filter } from 'rxjs/operators';
import { Discount, ListDiscountsRequestFiltersInterface, DiscountService } from '@galaxy/billing';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'store-base-pricing',
  template: '',
  standalone: false,
})
export class VaBasePricingComponent implements OnChanges {
  @Input() pricing: Pricing = null;
  @Input() billedProduct: BilledProduct;
  @Input() wrapFrequency = false;
  @Input() isAddon = false;
  @Input() hasVerifiedContract = false;
  @Input() highlightPrice = true;
  @Input() loaded = true;
  @Input() showAllPrices = false; // When false this will only show the first element in the pricing.prices list
  @Input() useAbbreviatedFrequency = false;
  @Input() showIsStartingAt = false;

  pricing$$ = new BehaviorSubject<Pricing>(null);
  billedProduct$$ = new BehaviorSubject<BilledProduct>(null);
  hasVerifiedContract$$ = new BehaviorSubject<boolean>(false);

  isFree$: Observable<boolean>;
  shouldContactSales$: Observable<boolean>;
  isFlatPrice$: Observable<boolean>;

  pricingTierData$: Observable<
    {
      isStartingPrice: boolean;
      pricingTierString: string;
      pricingTierForProduct: string;
      pricingTierForAddon: string;
      priceStringForTier: string;
      priceStringForTierWithoutFreq: string;
    }[]
  >;

  priceData$: Observable<
    {
      isStartingPrice: boolean;
      priceStringForPricingWithoutFrequency: string;
      priceStringForPricingWithFrequency: string;
      frequencyString: string;
      abbreviatedFrequencyString: string;
    }[]
  >;

  commitmentMessage$: Observable<string>;
  setupFeeString$: Observable<string>;
  isFirstTierFree$: Observable<boolean>;
  showPricingPopover = false;
  discounts$: Observable<Discount[]>;

  get pricing$(): Observable<Pricing> {
    return this.pricing$$.asObservable();
  }

  get billedProduct$(): Observable<BilledProduct> {
    return this.billedProduct$$.asObservable();
  }

  get hasVerifiedContract$(): Observable<boolean> {
    return this.hasVerifiedContract$$.asObservable();
  }

  constructor(
    private translateService: TranslateService,
    private displayPriceService: DisplayPriceService,
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    private discountService: DiscountService,
  ) {
    this.isFree$ = combineLatest([this.pricing$, this.billedProduct$, this.hasVerifiedContract$]).pipe(
      map(([pricing, billedProduct, hasVerifiedContract]) => {
        const isFreeHelper = (prices: { price: number }[]): boolean => {
          return prices ? prices.every((p) => p.price === 0 || p.price === undefined) : false;
        };

        if (hasVerifiedContract && billedProduct) {
          return billedProduct.pricingTiers.length === 1 ? isFreeHelper(billedProduct.pricingTiers) : false;
        }
        return !!pricing && isFreeHelper(pricing.prices);
      }),
    );

    this.shouldContactSales$ = combineLatest([this.pricing$, this.billedProduct$, this.hasVerifiedContract$]).pipe(
      map(([pricing, billedProduct, hasVerifiedContract]) => {
        const nestedPricesIsContactSales = (prices: { price: number }[]) => {
          return prices && prices.length > 0 ? prices.some((p) => p.price === null || p.price < 0) : true;
        };

        if (hasVerifiedContract && billedProduct) {
          return nestedPricesIsContactSales(billedProduct.pricingTiers);
        }
        if (!pricing) {
          return true;
        }
        return nestedPricesIsContactSales(pricing.prices);
      }),
    );

    this.isFlatPrice$ = this.billedProduct$.pipe(map((billedProduct) => billedProduct.pricingTiers.length === 1));
    this.pricingTierData$ = this.billedProduct$.pipe(
      switchMap((billedProduct) => {
        const pricingTierObservables = billedProduct.pricingTiers.map((pricingTier) => {
          return combineLatest([
            of(pricingTier.isStartingPrice),
            this.buildPricingTierString(pricingTier),
            this.buildPricingTierForProduct(pricingTier, billedProduct.billingFrequency),
            this.buildPricingTierForAddon(pricingTier),
            this.buildPriceStringForTier(pricingTier, billedProduct.billingFrequency, pricingTier.isStartingPrice),
            this.buildPriceStringForTier(pricingTier),
          ]).pipe(
            map(
              ([
                isStartingPrice,
                pricingTierString,
                pricingTierForProduct,
                pricingTierForAddon,
                priceStringForTier,
                priceStringForTierWithoutFreq,
              ]) => {
                return {
                  isStartingPrice: isStartingPrice,
                  pricingTierString: pricingTierString,
                  pricingTierForProduct: pricingTierForProduct,
                  pricingTierForAddon: pricingTierForAddon,
                  priceStringForTier: priceStringForTier,
                  priceStringForTierWithoutFreq: priceStringForTierWithoutFreq,
                };
              },
            ),
          );
        });
        return combineLatest([...pricingTierObservables]);
      }),
    );

    this.priceData$ = this.pricing$.pipe(
      switchMap((pricing) => {
        let filteredPrices = pricing.prices;
        if (pricing.prices.length > 1) {
          filteredPrices = pricing.prices.filter((price) => price.price > 0).sort(this.sortPricesByFrequency);
        }
        const priceObservables = filteredPrices.map((price) => {
          return combineLatest([
            of(price.isStartingPrice),
            this.buildPriceStringForPricing(price, pricing.currency, true, false),
            this.buildPriceStringForPricing(price, pricing.currency, false, true),
            this.buildFrequencyString(price.frequency),
            of(this.buildAbbreviatedFrequencyString(price.frequency)),
          ]).pipe(
            map(
              ([
                isStartingPrice,
                priceStringForPricingWithoutFrequency,
                priceStringForPricingWithFrequency,
                frequencyString,
                abbrevFreq,
              ]) => {
                return {
                  isStartingPrice: isStartingPrice,
                  priceStringForPricingWithoutFrequency: priceStringForPricingWithoutFrequency,
                  priceStringForPricingWithFrequency: priceStringForPricingWithFrequency,
                  frequencyString: frequencyString,
                  abbreviatedFrequencyString: abbrevFreq,
                };
              },
            ),
          );
        });
        return combineLatest([...priceObservables]);
      }),
    );

    this.commitmentMessage$ = this.buildCommitmentMessage();
    this.setupFeeString$ = this.displayPriceService.getSetupFeeString();
    this.isFirstTierFree$ = this.billedProduct$.pipe(
      map((billedProduct) => {
        return billedProduct.pricingTiers.length > 1 && billedProduct.pricingTiers.some((item) => item.price === 0);
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.prototype.hasOwnProperty.call(changes, 'pricing')) {
      this.pricing$$.next(changes.pricing.currentValue);
    }
    if (Object.prototype.hasOwnProperty.call(changes, 'billedProduct')) {
      this.billedProduct$$.next(changes.billedProduct.currentValue);
    }
    if (Object.prototype.hasOwnProperty.call(changes, 'hasVerifiedContract')) {
      this.hasVerifiedContract$$.next(changes.hasVerifiedContract.currentValue);
    }
  }

  buildPricingTierString(tier: PricingTier): Observable<string> {
    let max = String(tier.rangeMax);
    if (tier.rangeMax === -1) {
      max = '∞';
      if (tier.rangeMin === 0 || tier.rangeMin === 1) {
        return of('');
      }
    }
    return this.translateService.stream('FRONTEND.STORE.MIN_TO_MAX', {
      minRange: String(tier.rangeMin),
      maxRange: max,
    });
  }

  buildPricingTierForProduct(tier: PricingTier, frequency?: string): Observable<string> {
    return combineLatest([this.buildPricingTierString(tier), this.buildFrequencyString(frequency)]).pipe(
      switchMap(([pricingTier, formattedFrequency]) => {
        if (pricingTier) {
          return this.translateService.stream('FRONTEND.STORE.PRODUCT_PRICING_WITH_FREQUENCY', {
            pricingTier: pricingTier,
            frequency: formattedFrequency,
          });
        }
        return of(formattedFrequency);
      }),
    );
  }

  buildPricingTierForAddon(tier: PricingTier): Observable<string> {
    return this.buildPricingTierString(tier).pipe(map((pricingRange) => (pricingRange ? pricingRange : '')));
  }

  sortPricesByFrequency(priceA: Price, priceB: Price): number {
    // this map is coded based on frequency values described by the enums described in the marketplace packages vendasta
    // apis and the corresponding translations to strings laced throughout galaxy
    const priceFrequencyComparisonMap: Map<string, Map<string, number>> = new Map<string, Map<string, number>>([
      [
        'MONTHLY',
        new Map([
          ['MONTHLY', 0],
          ['YEARLY', -1],
          ['ONCE', -1],
          ['ONE TIME', -1],
          ['ONE_TIME', -1],
        ]),
      ],
      [
        'YEARLY',
        new Map([
          ['MONTHLY', 1],
          ['YEARLY', 0],
          ['ONCE', -1],
          ['ONE TIME', -1],
          ['ONE_TIME', -1],
        ]),
      ],
      [
        'ONCE',
        new Map([
          ['MONTHLY', 1],
          ['YEARLY', 1],
          ['ONCE', 0],
          ['ONE TIME', 0],
          ['ONE_TIME', 0],
        ]),
      ],
      [
        'ONE TIME',
        new Map([
          ['MONTHLY', 1],
          ['YEARLY', 1],
          ['ONCE', 0],
          ['ONE TIME', 0],
          ['ONE_TIME', 0],
        ]),
      ],
      [
        'ONE_TIME',
        new Map([
          ['MONTHLY', 1],
          ['YEARLY', 1],
          ['ONCE', 0],
          ['ONE TIME', 0],
          ['ONE_TIME', 0],
        ]),
      ],
    ]);

    if (priceFrequencyComparisonMap.get(priceA.frequency.toUpperCase()) === undefined) {
      return 0;
    }

    const compareVal = priceFrequencyComparisonMap
      .get(priceA.frequency.toUpperCase())
      .get(priceB.frequency.toUpperCase());
    return compareVal ? compareVal : 0;
  }

  buildPriceStringForPricing(
    price: Price,
    currency: string,
    excludeFrequency: boolean,
    canIncludeStartingAtPrefix: boolean,
  ): Observable<string> {
    return this.displayPriceService.formatDisplayPrice(
      price.price,
      currency,
      (excludeFrequency ? '' : price.frequency) as any,
      true,
      true,
      true,
      price.isStartingPrice && canIncludeStartingAtPrefix,
    );
  }

  buildPriceStringForTier(
    tier: PricingTier,
    frequency?: MarketplaceBillingFrequency,
    isStartingPrice?: boolean,
  ): Observable<string> {
    return this.displayPriceService.formatDisplayPrice(
      tier.price,
      this.billedProduct.currency,
      frequency as any,
      undefined,
      undefined,
      undefined,
      isStartingPrice,
    );
  }

  buildFrequencyString(frequency?: string): Observable<string> {
    return this.displayPriceService.formatBillingFrequency(frequency);
  }

  buildAbbreviatedFrequencyString(frequency?: string): string {
    switch ((frequency || '').toLowerCase()) {
      case 'daily':
        return 'dy';
      case 'weekly':
        return 'wk';
      case 'monthly':
        return 'mo';
      case 'yearly':
        return 'yr';
    }
    return null;
  }

  buildCommitmentMessage(): Observable<string> {
    return combineLatest([
      this.translateService.stream('FRONTEND.STORE.PRICING_FREQUENCIES'),
      this.billedProduct$,
      this.pricing$,
      this.isFree$,
      this.shouldContactSales$,
    ]).pipe(
      switchMap(([frequencyTranslations, billedProduct, pricing, isFree, shouldContactSales]) => {
        const frequency = billedProduct
          ? billedProduct.billingFrequency.toString().toLowerCase()
          : pricing && pricing.prices.length > 0
            ? pricing.prices[0].frequency.toLowerCase()
            : '';
        let frequencyString = '';
        if (frequency === 'yearly') {
          frequencyString = frequencyTranslations.YEAR;
        } else if (frequency === 'monthly') {
          frequencyString = frequencyTranslations.MONTH;
        }
        if (!frequencyString || isFree || shouldContactSales) {
          return of('');
        }
        if (billedProduct && billedProduct.commitment) {
          const initial = billedProduct.commitment.initial;
          const recurring = billedProduct.commitment.recurring;
          return this.translateService.stream('FRONTEND.STORE.COMMITMENT_WITH_RENEWAL', {
            initial: initial,
            frequencyString: frequencyString,
            recurring: recurring,
          });
        }
        return of('');
      }),
    );
  }

  private fetchAllDiscounts(pid: string, sku: string): Observable<Discount[]> {
    const pageSize = 50;
    const filters: ListDiscountsRequestFiltersInterface = {
      merchantId: pid,
      expiry: new Date(),
      skus: [sku],
    };

    return this.discountService.list(filters, '', pageSize).pipe(
      expand((resp) => {
        if ((resp?.results || []).length >= pageSize) {
          return this.discountService.list(filters, resp.nextCursor, pageSize);
        } else {
          return EMPTY;
        }
      }),
      reduce((prev, curr) => {
        const discounts = curr?.results || [];
        return prev.concat(discounts);
      }, [] as Discount[]),
    );
  }

  private getDiscounts(): Observable<Discount[]> {
    return combineLatest([this.partnerId$, this.billedProduct$]).pipe(
      filter(([pid, billedProduct]) => !!pid && !!billedProduct),
      switchMap(([pid, billedProduct]) => {
        return this.fetchAllDiscounts(pid, billedProduct.productId);
      }),
      catchError((err: HttpErrorResponse) => {
        console.error('error getting discount information:', err);
        return of([]);
      }),
      publishReplay(1),
      refCount(),
    );
  }

  itemInfoIconEnter(): void {
    if (this.discounts$ === undefined) {
      this.discounts$ = this.getDiscounts();
    }

    this.showPricingPopover = true;
  }

  itemInfoIconLeave(): void {
    this.showPricingPopover = false;
  }
}
